import type { IManagedUserSession } from '@/types';

/**
 * Handles clicks for shared callbacks in the manager dashboard, such as viewing
 * scorecards or starting certifications.
 *
 * Accepts a record of type IManagedUserSession and an optional callback function. If the callback
 * is provided, it will be invoked with the record as an argument.
 *
 * This function serves as a centralized handler for various actions that can be performed on a managed
 * user's session, ensuring consistent behavior across different components.
 *
 * @example
 * // Example usage for viewing a scorecard
 * <Button onClick={() => handleManagerSharedCallbackClick({ record, callback: onViewScorecardClick })}>
 *   View Scorecard
 * </Button>
 */
export const handleManagerSharedCallbackClick = ({
  record,
  callback,
}: {
  record: IManagedUserSession;
  callback?: (record: IManagedUserSession) => void;
}) => {
  return callback ? callback(record) : undefined;
};

/**
 * Handles product change events for shared callbacks in the manager dashboard.
 *
 * Accepts a product value and an optional callback function. If the callback is provided, it will be invoked
 * with the product value as an argument. This function is used to centralize the handling of product selection changes
 * across different components in the manager dashboard.
 *
 * @example
 * // Example usage for handling product change
 * <NativeSelect onChange={(value) => handleManagerSharedProductChangeCallback(value, onProductChange)} />
 */
export const handleManagerSharedProductChangeCallback = (
  value: string,
  callback?: (value: string) => void,
) => {
  return callback ? callback(value) : undefined;
};
